const PRODUCTION_ROOT_DOMAIN = 'retira.app'
const PRODUCTION_PUBLIC_HOSTS = new Set(['retira.app', 'www.retira.app'])
const PRODUCTION_STUDIO_HOST = 'studio.retira.app'
const LOCAL_STUDIO_HOST = 'studio.localhost'
const STUDIO_PATH_PREFIXES = ['/studio', '/admin', '/login', '/auth', '/fees']

function getCurrentLocation() {
  if (typeof window === 'undefined') {
    return {
      hostname: '',
      origin: '',
      pathname: '/',
      port: '',
      protocol: 'http:',
    }
  }

  return window.location
}

function normalizePath(path = '/') {
  const value = String(path || '/')
  return value.startsWith('/') ? value : `/${value}`
}

function getHostname(hostname) {
  return String(hostname ?? getCurrentLocation().hostname).toLowerCase()
}

export function isProductionPublicHost(hostname) {
  return PRODUCTION_PUBLIC_HOSTS.has(getHostname(hostname))
}

export function isProductionStudioHost(hostname) {
  return getHostname(hostname) === PRODUCTION_STUDIO_HOST
}

export function isLocalStudioHost(hostname) {
  return getHostname(hostname) === LOCAL_STUDIO_HOST
}

export function isStudioHost(hostname) {
  return isProductionStudioHost(hostname) || isLocalStudioHost(hostname)
}

// The registrable domain shared by the public (demo) and studio surfaces, so a
// cookie set on one is visible to the other. The Supabase session lives in
// origin-scoped localStorage, which the demo host can't read – a flag cookie on
// the shared parent domain is how we tell, from the demo, that the user is
// signed in on studio. Returns null for hosts without a known sibling surface
// (e.g. Vercel previews, raw IPs), where no cross-subdomain cookie is set.
export function getSharedCookieDomain(hostname) {
  const host = getHostname(hostname)
  if (host === PRODUCTION_ROOT_DOMAIN || host.endsWith(`.${PRODUCTION_ROOT_DOMAIN}`)) {
    return PRODUCTION_ROOT_DOMAIN
  }
  if (host === 'localhost' || host.endsWith('.localhost')) {
    return 'localhost'
  }
  return null
}

export function isStudioPath(pathname = getCurrentLocation().pathname) {
  const path = normalizePath(pathname)
  return STUDIO_PATH_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))
}

export function getRouteSurface({
  hostname = getCurrentLocation().hostname,
  pathname = getCurrentLocation().pathname,
} = {}) {
  if (isStudioHost(hostname)) return 'studio'
  if (isProductionPublicHost(hostname)) return 'public'
  return isStudioPath(pathname) ? 'studio' : 'public'
}

export function getStudioHomePath(hostname = getCurrentLocation().hostname) {
  return isStudioHost(hostname) ? '/' : '/studio'
}

export function getPublicHref(path = '/') {
  const targetPath = normalizePath(path)
  const location = getCurrentLocation()
  const hostname = getHostname(location.hostname)

  if (hostname === PRODUCTION_STUDIO_HOST) {
    return `https://www.retira.app${targetPath}`
  }

  if (hostname === LOCAL_STUDIO_HOST) {
    const port = location.port ? `:${location.port}` : ''
    return `${location.protocol}//localhost${port}${targetPath}`
  }

  return targetPath
}

export function getStudioHref(path = '/') {
  const targetPath = normalizePath(path)
  const location = getCurrentLocation()
  const hostname = getHostname(location.hostname)

  if (isProductionPublicHost(hostname)) {
    return `https://${PRODUCTION_STUDIO_HOST}${targetPath}`
  }

  if (targetPath === '/' && !isStudioHost(hostname)) {
    return '/studio'
  }

  return targetPath
}

export function isExternalHref(href) {
  return /^https?:\/\//i.test(String(href || ''))
}

export function navigateToHref(href, navigate, options) {
  if (isExternalHref(href)) {
    window.location.assign(href)
    return
  }

  navigate(href, options)
}

export function navigateToPublicPath(path, navigate, options) {
  navigateToHref(getPublicHref(path), navigate, options)
}

export function navigateToStudioPath(path, navigate, options) {
  navigateToHref(getStudioHref(path), navigate, options)
}
